/**
 * DG Shipping / STCW canonical certificate catalog.
 *
 * This module is the deterministic name-normalization layer between raw DG
 * profile PDF text and Seamigo compliance logic. The parser may extract
 * "PST Refresher" or "Personal Survival Techniques"; both should become the
 * same canonical family before lifecycle and dedupe decisions are made.
 */

import type { CertCategory } from '@/lib/db';
import type { LifecycleType } from '@/data/certificate-lifecycle-rules';

export type CertificateCatalogEntry = {
  canonicalId: string;
  canonicalName: string;
  shortCode?: string;
  aliases: string[];
  matchPatterns: RegExp[];
  stcwCode?: string;
  category: CertCategory;
  lifecycle: LifecycleType;
  cycleYears?: number;
  honorExplicitExpiry?: boolean;
  requirementNote: string;
  supersedes?: string[];
  sourceUrls: string[];
};

export type CertificateCatalogMatch = {
  entry: CertificateCatalogEntry;
  canonicalId: string;
  canonicalName: string;
  shortCode?: string;
  originalName: string;
};

const DG_TRAINING = 'https://www.dgshipping.gov.in/Content/TrainingInstitutes.aspx';
const DG_STCW_6_2011 = 'https://www.dgshipping.gov.in/viewNotice.aspx?noticeid=976';
const DG_ORIGINALS_NOTICE = 'https://www.dgshipping.gov.in/viewNotice.aspx?noticeid=1011';
const DG_PSSR_CIRCULAR = 'https://www.dgshipping.gov.in/viewNotice.aspx?noticeid=994';
const DG_EFA_GUIDELINES = 'https://www.dgshipping.gov.in/viewNotice.aspx?noticeid=493';
const DG_RPSCRB_2021 =
  'https://www.dgshipping.gov.in/writereaddata/ShippingNotices/202106170656128304779DGSCircular19of202117062021.pdf';
const DG_REFRESHER_EXIT_EXAM =
  'https://www.dgshipping.gov.in/writereaddata/News/202412051204158499495DiscontinuationofExitExamsforRefresherCourses.pdf';
const DG_MEDICAL = 'https://dgma.gov.in/maritime-health';
const DG_CDC = 'https://www.dgma.gov.in/crew-division/crew-continuous-discharge-certificate';
const DG_INDOS = 'https://www.dgshipping.gov.in/Content/INDOSInformation.aspx';
const IMO_STCW = 'https://www.imo.org/en/OurWork/HumanElement/Pages/STCW-Convention.aspx';
const PASSPORT_SEVA = 'https://portal2.passportindia.gov.in/AppOnlineProject/online/faqServicesAvailable';

export const CERTIFICATE_CATALOG: CertificateCatalogEntry[] = [
  {
    canonicalId: 'pst',
    canonicalName: 'Personal Survival Techniques',
    shortCode: 'PST',
    aliases: ['PST', 'PST Refresher', 'Personal Survival Techniques Refresher'],
    matchPatterns: [
      /\bpersonal\s+survival\s+techniques?\b/i,
      /\bpst\b/i,
    ],
    stcwCode: 'STCW Table A-VI/1-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/1 Table A-VI/1-1: 5-year refresher in Personal Survival Techniques required.',
    sourceUrls: [DG_STCW_6_2011, DG_ORIGINALS_NOTICE, DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'fpff',
    canonicalName: 'Fire Prevention & Fire Fighting',
    shortCode: 'FPFF',
    aliases: ['FPFF', 'FP & FF', 'Fire Prevention and Fire Fighting', 'Basic Fire-Fighting'],
    matchPatterns: [
      /fire\s*prevention\s*(?:and|&)\s*fire\s*[- ]?\s*fighting/i,
      /\bfp\s*&?\s*ff\b/i,
      /\bfpff\b/i,
      /basic\s+fire\s*[- ]?\s*fighting/i,
    ],
    stcwCode: 'STCW Table A-VI/1-2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/1 Table A-VI/1-2: 5-year refresher in Fire Prevention & Fire Fighting required.',
    supersedes: [],
    sourceUrls: [DG_STCW_6_2011, DG_ORIGINALS_NOTICE, DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'efa',
    canonicalName: 'Elementary First Aid',
    shortCode: 'EFA',
    aliases: ['EFA', 'Elementary First Aid'],
    matchPatterns: [/elementary\s+first\s+aid/i, /\befa\b/i],
    stcwCode: 'STCW Table A-VI/1-3',
    category: 'STCW',
    lifecycle: 'no_five_year_revalidation',
    requirementNote:
      'DGS does not list EFA among the competencies requiring a fixed five-year refresher. MFA satisfies the lower EFA requirement; verify any company or flag requirement.',
    sourceUrls: [DG_EFA_GUIDELINES, DG_STCW_6_2011, DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'pssr',
    canonicalName: 'Personal Safety & Social Responsibilities',
    shortCode: 'PSSR',
    aliases: ['PSSR', 'Personal Safety and Social Responsibilities'],
    matchPatterns: [/personal\s+safety.*social|social.*personal\s+safety/i, /\bpssr\b/i],
    stcwCode: 'STCW Table A-VI/1-4',
    category: 'STCW',
    lifecycle: 'no_five_year_revalidation',
    requirementNote:
      'DGS does not list PSSR among the competencies requiring a fixed five-year refresher. Verify any company-specific requirement.',
    sourceUrls: [DG_PSSR_CIRCULAR, DG_STCW_6_2011],
  },
  {
    canonicalId: 'pscrb',
    canonicalName: 'Proficiency in Survival Craft & Rescue Boats',
    shortCode: 'PSCRB',
    aliases: ['PSCRB', 'RPSCRB', 'PSCRB Refresher', 'Survival Craft and Rescue Boats'],
    matchPatterns: [
      /proficiency\s+in\s+survival\s+craft/i,
      /survival\s+craft.*rescue\s+boats?/i,
      /\brpscrb\b/i,
      /\bpscrb\b/i,
    ],
    stcwCode: 'STCW Reg. VI/2-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/2-1: 5-year refresher for Proficiency in Survival Craft & Rescue Boats required.',
    supersedes: [],
    sourceUrls: [DG_STCW_6_2011, DG_ORIGINALS_NOTICE, DG_REFRESHER_EXIT_EXAM, DG_RPSCRB_2021],
  },
  {
    canonicalId: 'frb',
    canonicalName: 'Proficiency in Fast Rescue Boats',
    shortCode: 'FRB',
    aliases: ['FRB', 'PFRB', 'Fast Rescue Boat'],
    matchPatterns: [/fast\s+rescue\s+boats?/i, /\bpfrb\b/i, /\bfrb\b/i],
    stcwCode: 'STCW Reg. VI/2-2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/2-2: 5-year refresher for Proficiency in Fast Rescue Boats required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'aff',
    canonicalName: 'Advanced Fire Fighting',
    shortCode: 'AFF',
    aliases: ['AFF', 'ADFF', 'Advance Firefighting', 'Advanced Fire-Fighting', 'AFF Refresher'],
    matchPatterns: [
      /advanc(?:e|ed)(?:\s+training\s+(?:for|in)\s+)?\s*fire\s*[- ]?\s*fighting/i,
      /advanc(?:e|ed)\s+firefighting/i,
      /fire\s*[- ]?\s*fighting.*advanc(?:e|ed)/i,
      /\baff\b/i,
      /\badff\b/i,
    ],
    stcwCode: 'STCW Reg. VI/3',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/3: 5-year refresher in Advanced Fire Fighting required for designated officers.',
    supersedes: ['fpff'],
    sourceUrls: [DG_STCW_6_2011, DG_ORIGINALS_NOTICE, DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'medical_first_aid',
    canonicalName: 'Medical First Aid',
    shortCode: 'MFA',
    aliases: ['MFA', 'MEFA', 'Medical First Aid'],
    matchPatterns: [/medical\s+first\s+aid/i, /\bmfa\b/i, /\bmefa\b/i],
    stcwCode: 'STCW Reg. VI/4-1',
    category: 'STCW',
    lifecycle: 'no_five_year_revalidation',
    requirementNote:
      'STCW Reg. VI/4-1: Medical First Aid. DG Shipping MS Notice 13 of 2020 clarifies no STCW five-year revalidation requirement; verify employer or flag-state requirement.',
    supersedes: [],
    sourceUrls: [DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'medical_care',
    canonicalName: 'Medical Care',
    shortCode: 'MCAS',
    aliases: ['Medical Care', 'Medical Care Officer', 'MCAS'],
    matchPatterns: [/medical\s+care\s+officer/i, /medical\s+care(?!\s+first)/i, /\bmcas\b/i],
    stcwCode: 'STCW Reg. VI/4-2',
    category: 'STCW',
    lifecycle: 'no_five_year_revalidation',
    requirementNote:
      'STCW Reg. VI/4-2: Medical Care. DG Shipping MS Notice 13 of 2020 clarifies no STCW five-year revalidation requirement; verify employer or flag-state requirement.',
    sourceUrls: [DG_REFRESHER_EXIT_EXAM],
  },
  {
    canonicalId: 'goc',
    canonicalName: 'GMDSS General Operator Certificate',
    shortCode: 'GOC',
    aliases: ['GOC', 'General Operator Certificate', 'GMDSS GOC'],
    matchPatterns: [/\bgoc\b/i, /general\s+operator\s+certificate/i],
    stcwCode: 'STCW Reg. IV/2',
    category: 'STCW',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. IV/2: GMDSS General Operator Certificate must be revalidated every 5 years.',
    supersedes: ['roc'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'roc',
    canonicalName: 'GMDSS Restricted Operator Certificate',
    shortCode: 'ROC',
    aliases: ['ROC', 'Restricted Operator Certificate', 'GMDSS ROC'],
    matchPatterns: [/\broc\b/i, /restricted\s+operator\s+certificate/i],
    stcwCode: 'STCW Reg. IV/2',
    category: 'STCW',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. IV/2: GMDSS Restricted Operator Certificate must be revalidated every 5 years.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'gmdss',
    canonicalName: 'GMDSS Radio Operator Certificate',
    shortCode: 'GMDSS',
    aliases: ['GMDSS', 'Global Maritime Distress and Safety System'],
    matchPatterns: [/\bgmdss\b/i, /global\s+maritime\s+distress/i],
    stcwCode: 'STCW Reg. IV/2',
    category: 'STCW',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. IV/2: GMDSS operator certificate must be revalidated every 5 years.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'sso',
    canonicalName: 'Ship Security Officer',
    shortCode: 'SSO',
    aliases: ['SSO', 'Ship Security Officer'],
    matchPatterns: [/ship\s+security\s+officer/i, /\bsso\b/i],
    stcwCode: 'STCW Reg. VI/5',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/5: 5-year refresher for Ship Security Officer certificate of proficiency required.',
    supersedes: ['security_awareness'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'security_awareness',
    canonicalName: 'Security Awareness / Designated Security Duties',
    shortCode: 'STSDSD',
    aliases: ['Security Awareness', 'Ship Security Awareness', 'Designated Security Duties', 'SSA', 'DSD', 'STSDSD', 'ISPS'],
    matchPatterns: [/security\s+awareness/i, /designated\s+security\s+duties/i, /\bstsdsd\b/i, /\bisps\b/i, /ship\s+security(?!\s+officer)/i],
    stcwCode: 'STCW Reg. VI/6',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. VI/6: 5-year refresher in Security Awareness / Designated Security Duties required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'dc_oil_tanker_endorsement',
    canonicalName: 'DC Endorsement - Oil Tanker',
    shortCode: 'DC Oil',
    aliases: ['DC Oil Tanker Endorsement', 'DC Endorsement Oil Tanker', 'Oil Tanker DC Endorsement'],
    matchPatterns: [
      /dc\s+endorsement.*oil\s+tanker/i,
      /oil\s+tanker.*dc\s+endorsement/i,
      /endorsement.*oil\s+tanker/i,
      /oil\s+tanker.*endorsement/i,
    ],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'Endorsement',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: Oil tanker DC endorsement is a distinct endorsement family. Track it separately from chemical and gas endorsements.',
    sourceUrls: [IMO_STCW, DG_TRAINING],
  },
  {
    canonicalId: 'dc_chemical_tanker_endorsement',
    canonicalName: 'DC Endorsement - Chemical Tanker',
    shortCode: 'DC Chemical',
    aliases: ['DC Chemical Tanker Endorsement', 'DC Endorsement Chemical Tanker', 'Chemical Tanker DC Endorsement'],
    matchPatterns: [
      /dc\s+endorsement.*chemical\s+tanker/i,
      /chemical\s+tanker.*dc\s+endorsement/i,
      /endorsement.*chemical\s+tanker/i,
      /chemical\s+tanker.*endorsement/i,
    ],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'Endorsement',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: Chemical tanker DC endorsement is a distinct endorsement family. Track it separately from oil and gas endorsements.',
    sourceUrls: [IMO_STCW, DG_TRAINING],
  },
  {
    canonicalId: 'dc_gas_tanker_endorsement',
    canonicalName: 'DC Endorsement - Gas Tanker',
    shortCode: 'DC Gas',
    aliases: ['DC Gas Tanker Endorsement', 'DC Endorsement Gas Tanker', 'Gas Tanker DC Endorsement'],
    matchPatterns: [
      /dc\s+endorsement.*(?:gas|lpg|lng|liquefied)\s+tanker/i,
      /(?:gas|lpg|lng|liquefied)\s+tanker.*dc\s+endorsement/i,
      /endorsement.*(?:gas|lpg|lng|liquefied)\s+tanker/i,
      /(?:gas|lpg|lng|liquefied)\s+tanker.*endorsement/i,
    ],
    stcwCode: 'STCW Reg. V/1-2',
    category: 'Endorsement',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-2: Gas tanker DC endorsement is a distinct endorsement family. Track it separately from oil and chemical endorsements.',
    sourceUrls: [IMO_STCW, DG_TRAINING],
  },
  {
    canonicalId: 'advanced_oil_tanker',
    canonicalName: 'Advanced Oil Tanker Training',
    shortCode: 'ATOT',
    aliases: ['ATOT', 'Advanced Oil Tanker Training'],
    matchPatterns: [/advanced.*oil\s+tanker/i, /advanced\s+tanker.*oil/i, /\batot\b/i],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: 5-year refresher for advanced oil tanker training endorsement required.',
    supersedes: ['basic_oil_tanker'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'basic_oil_tanker',
    canonicalName: 'Basic Oil Tanker Training',
    shortCode: 'BOT',
    aliases: ['BOTT', 'BOT Oil', 'Basic Oil Tanker Training'],
    matchPatterns: [/basic.*oil\s+tanker/i, /basic\s+tanker.*oil/i, /\bbot\b.*oil/i, /oil.*\bbot\b/i, /\bbott\b/i],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: 5-year refresher for basic oil tanker training endorsement required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'advanced_chemical_tanker',
    canonicalName: 'Advanced Chemical Tanker Training',
    shortCode: 'ATCT',
    aliases: ['ATCT', 'Advanced Chemical Tanker Training'],
    matchPatterns: [/advanced.*chemical\s+tanker/i, /advanced\s+tanker.*chemical/i, /\batct\b/i],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: 5-year refresher for advanced chemical tanker training endorsement required.',
    supersedes: ['basic_chemical_tanker'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'basic_chemical_tanker',
    canonicalName: 'Basic Chemical Tanker Training',
    shortCode: 'BCT',
    aliases: ['BCTT', 'Basic Chemical Tanker Training'],
    matchPatterns: [/basic.*chemical\s+tanker/i, /basic\s+tanker.*chemical/i, /\bbot\b.*chem/i, /chem.*\bbot\b/i, /\bbctt\b/i],
    stcwCode: 'STCW Reg. V/1-1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-1: 5-year refresher for basic chemical tanker training endorsement required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'advanced_gas_tanker',
    canonicalName: 'Advanced Gas Tanker Training',
    shortCode: 'ATGT',
    aliases: ['ATGT', 'Advanced Gas Tanker Training', 'Advanced LPG Tanker', 'Advanced LNG Tanker'],
    matchPatterns: [/advanced.*(?:gas|lpg|lng|liquefied).*tanker/i, /advanced\s+tanker.*(?:gas|lpg|lng)/i, /\batgt\b/i],
    stcwCode: 'STCW Reg. V/1-2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-2: 5-year refresher for advanced gas tanker training endorsement required.',
    supersedes: ['basic_gas_tanker'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'basic_gas_tanker',
    canonicalName: 'Basic Gas Tanker Training',
    shortCode: 'BGT',
    aliases: ['Basic Gas Tanker Training', 'Basic LPG Tanker', 'Basic LNG Tanker'],
    matchPatterns: [/basic.*(?:gas|lpg|lng|liquefied).*tanker/i, /basic\s+tanker.*(?:gas|lpg|lng)/i],
    stcwCode: 'STCW Reg. V/1-2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/1-2: 5-year refresher for basic gas tanker training endorsement required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'dangerous_goods',
    canonicalName: 'Dangerous Goods / IMDG',
    shortCode: 'IMDG',
    aliases: ['Dangerous Goods', 'IMDG', 'IMSBC', 'IMO DG', 'IMDG Dangerous Goods Awareness', 'IMDG Awareness', 'Dangerous Goods Combined'],
    matchPatterns: [/dangerous\s+goods/i, /\bimdg\b/i, /\bimsbc\b/i, /imo\s+dg\b/i],
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW / IMDG Code: 5-year refresher in Dangerous Goods handling required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'ecdis',
    canonicalName: 'Electronic Chart Display and Information System',
    shortCode: 'ECDIS',
    aliases: ['ECDIS', 'ECDIS Refresher', 'ECDIS Revalidation', 'ECDIS Update', 'ECDIS Generic', 'Electronic Chart Display'],
    matchPatterns: [/\becdis\b/i, /electronic\s+chart\s+display/i],
    stcwCode: 'STCW Reg. II',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II: 5-year refresher in ECDIS proficiency required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'arpa',
    canonicalName: 'ARPA / Radar',
    shortCode: 'ARPA',
    aliases: ['ARPA', 'ARPA Operational Level', 'ARPA Management Level', 'Radar/ARPA', 'Radar Simulator', 'Radar Observer'],
    matchPatterns: [/\barpa\b/i, /radar\s+simulator/i, /radar\s+observer/i, /radar.*(?:operational|management)/i, /(?:operational|management).*radar/i, /radar\s*\/\s*arpa/i],
    stcwCode: 'STCW Reg. II',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II: 5-year refresher in ARPA/Radar proficiency required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'brm',
    canonicalName: 'Bridge Resource Management',
    shortCode: 'BRM',
    aliases: ['BRM', 'BRM Refresher', 'Bridge Resource Management', 'Bridge Resource Management Refresher'],
    matchPatterns: [/bridge\s+resource\s+management/i, /\bbrm\b/i],
    stcwCode: 'STCW Reg. II',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II: 5-year refresher in Bridge Resource Management required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'sar',
    canonicalName: 'Search and Rescue Coordinator',
    shortCode: 'SAR',
    aliases: ['SAR', 'Search and Rescue Coordinator'],
    matchPatterns: [/search\s+and\s+rescue/i, /\bsar\b.*coordinator/i, /coordinator.*\bsar\b/i],
    stcwCode: 'STCW Reg. II',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II: 5-year refresher in Search and Rescue coordination required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'erm',
    canonicalName: 'Engine Room Resource Management',
    shortCode: 'ERM',
    aliases: ['ERM', 'ERRM', 'Engine Room Resource Management', 'ERM Refresher', 'Engine Room Resource Management Refresher'],
    matchPatterns: [/engine\s+r(?:oom|esource)\s+(?:resource|management)/i, /\berm\b/i, /\berrm\b/i],
    stcwCode: 'STCW Reg. III',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. III: 5-year refresher in Engine Room Resource Management required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'eto',
    canonicalName: 'Electro-Technical Officer',
    shortCode: 'ETO',
    aliases: ['ETO', 'Electro Technical Officer', 'Electro-Technical Officer', 'Electrotechnical Officer', 'ETO Course', 'ETO Training', 'Electro-Technical Course'],
    matchPatterns: [/electro.?technical\s+officer/i, /\belectrotechnical\b/i, /electro.?technical\s+course/i, /\beto\b/i],
    stcwCode: 'STCW Reg. III/6',
    category: 'STCW',
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. III/6: ETO certificate of competency must be revalidated every 5 years.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'high_voltage',
    canonicalName: 'High Voltage Safety',
    shortCode: 'HV',
    aliases: ['High Voltage', 'HV Training', 'HV Safety', 'High Voltage Safety', 'High Voltage Training'],
    matchPatterns: [/high\s+voltage/i, /\bhv\b.*train/i, /hv.*(?:course|safety)/i],
    stcwCode: 'STCW Reg. III',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. III: 5-year refresher in High Voltage safety required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'cmhb',
    canonicalName: 'Crisis Management & Human Behaviour',
    shortCode: 'CMHB',
    aliases: ['CMHB', 'Crisis Management and Human Behaviour'],
    matchPatterns: [/crisis\s+management.*human/i, /human\s+behaviour.*crisis/i, /\bcmhb\b/i, /passenger\s+ship.*crisis/i],
    stcwCode: 'STCW Reg. V/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Crisis Management & Human Behaviour required for passenger ship officers.',
    supersedes: ['crowd_management'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'crowd_management',
    canonicalName: 'Crowd Management',
    aliases: ['Crowd Management'],
    matchPatterns: [/crowd\s+management/i],
    stcwCode: 'STCW Reg. V/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Crowd Management required for passenger ship personnel.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'passenger_safety',
    canonicalName: 'Passenger Ship Safety',
    aliases: ['Passenger Safety', 'Passenger Ship Safety'],
    matchPatterns: [/passenger.{0,10}safety/i, /safety.{0,10}passenger/i],
    stcwCode: 'STCW Reg. V/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Passenger Safety required.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'leadership_managerial',
    canonicalName: 'Leadership & Managerial Skills',
    aliases: ['Leadership and Managerial Skills', 'Managerial Skills'],
    matchPatterns: [/leadership.*managerial/i, /managerial.*skill/i],
    stcwCode: 'STCW Reg. II/2 / III/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II/2 / III/2: 5-year refresher in Leadership & Managerial Skills required for management-level officers.',
    supersedes: ['leadership_teamwork'],
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'leadership_teamwork',
    canonicalName: 'Leadership & Teamworking Skills',
    aliases: ['Leadership and Teamwork', 'Leadership and Teamworking Skills'],
    matchPatterns: [/leadership.*teamwork/i, /teamwork.*(?:skills|leadership)/i],
    stcwCode: 'STCW Reg. II/1 / III/1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. II/1 / III/1: 5-year refresher in Leadership & Teamworking Skills required for operational-level officers.',
    sourceUrls: [DG_TRAINING],
  },
  // ── Basic Safety Training (family group) ────────────────────────────────────
  {
    canonicalId: 'bst',
    canonicalName: 'Basic Safety Training',
    shortCode: 'BST',
    aliases: ['BST', 'BST Refresher', 'BST Revalidation', 'Basic Safety Training'],
    matchPatterns: [/\bbst\b/i, /basic\s+safety\s+training/i],
    stcwCode: 'STCW Reg. VI/1',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'DGS requires five-year refresher competency for the PST and FPFF components of Basic Safety Training. Verify which components the BST record covers.',
    sourceUrls: [DG_STCW_6_2011, DG_TRAINING],
  },

  // ── Helicopter Underwater Escape Training ────────────────────────────────────
  {
    canonicalId: 'huet',
    canonicalName: 'Helicopter Underwater Escape Training',
    shortCode: 'HUET',
    aliases: ['HUET', 'HUET Refresher', 'Helicopter Underwater Escape'],
    matchPatterns: [/\bhuet\b/i, /helicopter\s+underwater\s+escape/i, /underwater\s+escape.*helicopter/i],
    stcwCode: 'STCW Reg. VI/1 (offshore)',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 4,
    requirementNote:
      'OPITO / DG Approved Centre: HUET requires refresher every 4 years for offshore/helicopter-transfer crew.',
    sourceUrls: [DG_TRAINING],
  },

  // ── Medical Fitness Certificate (ENG1) ───────────────────────────────────────
  {
    canonicalId: 'medical_fitness',
    canonicalName: 'Medical Fitness Certificate',
    shortCode: 'ENG1',
    aliases: ['ENG1', 'Medical Fitness Certificate', 'Medical Fitness', 'Seafarer Medical Certificate'],
    matchPatterns: [
      /\beng1\b/i,
      /medical\s+fitness\s+certificate/i,
      /seafarer\s+medical\s+(?:fitness|certificate)/i,
      /fitness\s+certificate.*sea(?:farer)?/i,
    ],
    category: 'Medical',
    lifecycle: 'expiry_date_based',
    cycleYears: 2,
    requirementNote:
      'MLC / DGMA: Medical fitness certificate valid 2 years (1 year for seafarers under 18 or over 65). Renewed on explicit expiry date — not a course refresher.',
    sourceUrls: [DG_MEDICAL],
  },

  // ── Authorised Documents ─────────────────────────────────────────────────────
  {
    canonicalId: 'indos',
    canonicalName: 'INDoS Number',
    shortCode: 'INDoS',
    aliases: ['INDoS', 'INDoS Number', 'Indian National Database of Seafarers'],
    matchPatterns: [/\bindos\b/i, /indian\s+national\s+database\s+of\s+seafarers/i],
    category: 'Flag State',
    lifecycle: 'no_expiry',
    requirementNote:
      'DG Shipping India: INDoS number is a lifetime unique seafarer identifier. No expiry.',
    sourceUrls: [DG_INDOS],
  },
  {
    canonicalId: 'exn',
    canonicalName: 'Exemption / Exit Notation (EXN)',
    shortCode: 'EXN',
    aliases: ['EXN', 'EXN-45', 'EXN 45', 'Exit Notation', 'Exemption Certificate'],
    matchPatterns: [/\bexn\b/i, /\bexn[-\s]?45\b/i, /exit\s+notation/i, /exemption\s+(?:certificate|notation)/i],
    honorExplicitExpiry: true,
    category: 'Flag State',
    lifecycle: 'no_expiry',
    requirementNote:
      'DG Shipping India: EXN / Exit Notation is a permanent record unless the issuing authority specifies an expiry date.',
    sourceUrls: [DG_ORIGINALS_NOTICE],
  },
  {
    canonicalId: 'cdc',
    canonicalName: 'Continuous Discharge Certificate',
    shortCode: 'CDC',
    aliases: ['CDC', 'Continuous Discharge Certificate', 'Continuous Discharge Book'],
    matchPatterns: [/\bcdc\b/i, /continuous\s+discharge\s+(?:certificate|book)/i],
    category: 'Flag State',
    lifecycle: 'expiry_date_based',
    cycleYears: 10,
    requirementNote:
      'MMD / DGMA India: CDC valid 10 years — renewed on explicit expiry date. Not a course refresher.',
    sourceUrls: [DG_CDC],
  },
  {
    canonicalId: 'passport',
    canonicalName: 'Indian Passport',
    shortCode: 'Passport',
    aliases: ['Passport', 'Indian Passport'],
    matchPatterns: [/\bpassport\b/i, /indian\s+passport/i],
    category: 'Flag State',
    lifecycle: 'expiry_date_based',
    cycleYears: 10,
    requirementNote:
      'Government of India: Passport valid 10 years — renewed on explicit expiry date. Not a course refresher.',
    sourceUrls: [PASSPORT_SEVA],
  },

  // ── Passenger ship additional ────────────────────────────────────────────────
  {
    canonicalId: 'passenger_familiarization',
    canonicalName: 'Passenger Ship Familiarization',
    shortCode: 'PSF',
    aliases: ['PSF', 'Passenger Ship Familiarization', 'Passenger Familiarization'],
    matchPatterns: [
      /passenger\s+ship\s+familiari[sz]ation/i,
      /passenger\s+familiari[sz]ation/i,
      /\bpsf\b/i,
    ],
    stcwCode: 'STCW Reg. V/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Passenger Ship Familiarization required for all crew on passenger ships.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'roro_safety',
    canonicalName: 'Ro-Ro Passenger Ship Safety',
    shortCode: 'Ro-Ro Safety',
    aliases: ['Ro-Ro Safety', 'Ro-Ro Passenger Ship Safety', 'RoRo Safety'],
    matchPatterns: [/ro.?ro\s+passenger/i, /ro.?ro.*safety/i, /\broro\b/i],
    stcwCode: 'STCW Reg. V/2',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Ro-Ro Passenger Ship Safety required for crew on Ro-Ro passenger ships.',
    sourceUrls: [DG_TRAINING],
  },

  // ── Specialized / additional STCW ───────────────────────────────────────────
  {
    canonicalId: 'enclosed_space',
    canonicalName: 'Enclosed Space Entry & Rescue',
    shortCode: 'ESE',
    aliases: ['ESE', 'Enclosed Space Entry', 'Enclosed Space Entry and Rescue', 'Confined Space Entry'],
    matchPatterns: [/enclosed\s+space\s+entry/i, /confined\s+space.*entry/i, /\bese\b/i],
    category: 'STCW',
    lifecycle: 'unknown_verify',
    requirementNote:
      'SOLAS / STCW: Enclosed space entry training required, but no fixed DG-approved refresher cycle confirmed. Verify renewal requirement with employer or flag state.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'marpol',
    canonicalName: 'MARPOL / Pollution Prevention',
    shortCode: 'MARPOL',
    aliases: ['MARPOL', 'Pollution Prevention', 'MARPOL Awareness'],
    matchPatterns: [/\bmarpol\b/i, /pollution\s+prevention/i, /oily\s+water\s+separator/i],
    category: 'Proficiency',
    lifecycle: 'unknown_verify',
    requirementNote:
      'MARPOL 73/78: Pollution prevention awareness training required; no fixed DG-approved refresher cycle confirmed. Verify renewal requirement with employer or flag state.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'polar_waters',
    canonicalName: 'Polar Waters Operations',
    shortCode: 'Polar',
    aliases: ['Polar Waters', 'Polar Code', 'Polar Operations'],
    matchPatterns: [/polar\s+waters?/i, /polar\s+code/i, /polar\s+operations?/i, /arctic.*operations?/i],
    stcwCode: 'STCW Reg. V/4',
    category: 'STCW',
    lifecycle: 'refresher_required',
    cycleYears: 5,
    requirementNote:
      'STCW Reg. V/4 (Polar Code): 5-year refresher in Polar Waters Operations required for Masters and deck officers on polar routes.',
    sourceUrls: [IMO_STCW],
  },
  {
    canonicalId: 'mrm',
    canonicalName: 'Maritime Resource Management',
    shortCode: 'MRM',
    aliases: ['MRM', 'Maritime Resource Management'],
    matchPatterns: [/maritime\s+resource\s+management/i, /\bmrm\b/i],
    category: 'STCW',
    lifecycle: 'unknown_verify',
    requirementNote:
      'STCW: Maritime Resource Management training required for officers; no fixed DG-approved standalone refresher cycle confirmed. Verify renewal requirement with employer or flag state.',
    sourceUrls: [DG_TRAINING],
  },
  {
    canonicalId: 'ers',
    canonicalName: 'Engine Room Simulator',
    shortCode: 'ERS',
    aliases: ['ERS', 'Engine Room Simulator'],
    matchPatterns: [/engine\s+room\s+simulator/i, /\bers\b/i],
    stcwCode: 'STCW Reg. III/1',
    category: 'STCW',
    lifecycle: 'unknown_verify',
    requirementNote:
      'STCW Reg. III/1: Engine room simulator training required for engineers; no fixed DG-approved standalone refresher cycle confirmed. Verify renewal requirement with employer or flag state.',
    sourceUrls: [DG_TRAINING],
  },
];

export function normalizeCertificateName(name: string): CertificateCatalogMatch | null {
  const originalName = name?.trim() ?? '';
  if (!originalName) return null;

  // Combined/refresher PSCRB certificates may explicitly mention included PST
  // competencies. The primary certificate remains PSCRB, so resolve its
  // distinctive acronym before the broader PST name matcher.
  if (/\br?pscrb\b/i.test(originalName)) {
    const entry = getCatalogEntryById('pscrb');
    if (entry) {
      return {
        entry,
        canonicalId: entry.canonicalId,
        canonicalName: entry.canonicalName,
        shortCode: entry.shortCode,
        originalName,
      };
    }
  }

  for (const entry of CERTIFICATE_CATALOG) {
    if (entry.matchPatterns.some((pattern) => pattern.test(originalName))) {
      return {
        entry,
        canonicalId: entry.canonicalId,
        canonicalName: entry.canonicalName,
        shortCode: entry.shortCode,
        originalName,
      };
    }
  }

  return null;
}

export function getCatalogEntryById(canonicalId: string): CertificateCatalogEntry | undefined {
  return CERTIFICATE_CATALOG.find((entry) => entry.canonicalId === canonicalId);
}
